import { Reorder } from "framer-motion";
import { AlertTriangle, Check, Film, GripVertical, Link2, Loader2, Star, Trash2, UploadCloud, RefreshCw, X, Youtube } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BACKEND_URL, api } from "@/lib/api";
import { ACCEPT_VIDEOS, discardVideoDirectUpload, uploadErrorMessage, uploadVideoDirect, videoPosterSrc } from "@/lib/uploads";
import { VIDEO_CATEGORIES } from "@/config/media";

/**
 * Unified horse video manager (Block 4 item 4).
 * Mixed sources side by side (direct Stream uploads | YouTube | Vimeo), drag to
 * reorder, star = main video (public hero player), per-video category,
 * replace/delete. Uploads run in the background with real progress — the
 * form stays fully editable; only Save waits for uploads to settle.
 *
 * Controlled: value = ordered MediaAsset[], onChange(next), mainId/onMainChange.
 */
export const VideoManager = ({ value, onChange, mainId, onMainChange, uploadsBusy, onUploadsBusy }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);
  const [uploads, setUploads] = useState([]); // [{key, name, pct, status: uploading|error, file, category, session}]
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // background-processing poll: refresh poster/status until everything is ready
  useEffect(() => {
    const processing = value.some((v) => v.video_status === "processing");
    if (!processing) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/media", { params: { kind: "video" } });
        const byId = Object.fromEntries(data.map((m) => [m.id, m]));
        onChange(value.map((v) => (byId[v.id] ? { ...v, ...byId[v.id] } : v)));
      } catch {
        /* next tick */
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.map((v) => `${v.id}:${v.video_status}`).join(",")]);

  useEffect(() => {
    onUploadsBusy?.(uploads.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads.length]);

  /* Direct Creator Upload a Stream con estado recuperable: un corte de red
     deja la fila en "error" con Reintentar — TUS reanuda desde el offset ya
     subido, sin reenviar bytes ni tocar el perfil del caballo. */
  const runUpload = (key, file, category, session) => {
    setUploads((u) => u.map((x) => (x.key === key ? { ...x, status: "uploading" } : x)));
    uploadVideoDirect(file, {
      category,
      session,
      onProgress: (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
    })
      .then((asset) => {
        setUploads((u) => u.filter((x) => x.key !== key));
        onChange((prev) => [...prev, asset]);
      })
      .catch((err) => {
        if (err.isTransportError) {
          // recuperable: conservar fichero + sesión para reanudar
          setUploads((u) => u.map((x) => (x.key === key
            ? { ...x, status: "error", session: err.uploadSession || x.session }
            : x)));
          return;
        }
        setUploads((u) => u.filter((x) => x.key !== key));
        toast.error(`${file.name}: ${uploadErrorMessage(err, t)}`);
      });
  };

  const startUploads = (files) => {
    for (const file of files) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      setUploads((u) => [...u, { key, name: file.name, pct: 0, status: "uploading", file, category: null, session: null }]);
      runUpload(key, file, null, null);
    }
  };

  const retryUpload = (u) => runUpload(u.key, u.file, u.category, u.session);

  const discardUpload = (u) => {
    setUploads((prev) => prev.filter((x) => x.key !== u.key));
    if (u.session?.uid) discardVideoDirectUpload(u.session.uid);
  };

  const onPick = (e) => {
    startUploads(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    startUploads(Array.from(e.dataTransfer.files || []).filter(
      (f) => f.type.startsWith("video/") || /\.(mp4|mov|m4v|webm|avi|mpe?g|mkv)$/i.test(f.name)));
  };

  const importUrl = async () => {
    if (!url.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post("/media/import-video", { url: url.trim() });
      onChange((prev) => [...prev, data]);
      setUrl("");
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      toast.error(code === "video_unavailable" ? t("vm.videoUnavailable")
        : code === "unrecognized_video_url" ? t("vm.urlUnrecognized")
        : t("vm.importFailed"));
    } finally {
      setImporting(false);
    }
  };

  const setCategory = async (id, category) => {
    await api.patch(`/media/${id}`, { category: category || null }).catch(() => {});
    onChange((prev) => prev.map((v) => (v.id === id ? { ...v, category } : v)));
  };

  const remove = async (video) => {
    onChange((prev) => prev.filter((v) => v.id !== video.id));
    if (mainId === video.id) onMainChange(null);
    await api.delete(`/media/${video.id}`).catch(() => {});
  };

  const beginReplace = (video) => {
    replaceTarget.current = video;
    replaceRef.current?.click();
  };

  const onReplacePick = async (e) => {
    const file = e.target.files?.[0];
    const target = replaceTarget.current;
    e.target.value = "";
    if (!file || !target) return;
    const key = `replace-${target.id}`;
    setUploads((u) => [...u, { key, name: file.name, pct: 0, status: "uploading", file, category: target.category || null, session: null }]);
    try {
      const asset = await uploadVideoDirect(file, {
        category: target.category || null,
        onProgress: (pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
      });
      onChange((prev) => prev.map((v) => (v.id === target.id ? asset : v)));  // same position
      if (mainId === target.id) onMainChange(asset.id);
      await api.delete(`/media/${target.id}`).catch(() => {});
    } catch (err) {
      // el vídeo original queda INTACTO: solo se sustituye tras un éxito completo
      if (err.uploadSession?.uid) discardVideoDirectUpload(err.uploadSession.uid);
      toast.error(err.isTransportError ? t("vm.uploadInterrupted") : uploadErrorMessage(err, t));
    } finally {
      setUploads((u) => u.filter((x) => x.key !== key));
    }
  };

  const providerBadge = (v) => {
    if (v.provider === "youtube") return { label: "YouTube", cls: "bg-[#FF0000]/10 text-[#B00000]" };
    if (v.provider === "vimeo") return { label: "Vimeo", cls: "bg-[#17A5E6]/10 text-[#0F6E99]" };
    return { label: t("vm.uploadBadge"), cls: "bg-[#1D4D3B]/10 text-[#1D4D3B]" };
  };

  return (
    <div className="flex flex-col gap-4" data-testid="video-manager">
      {/* drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t("vm.dropHint")}
        data-testid="video-dropzone"
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed px-6 py-8 flex flex-col items-center gap-2 text-center cursor-pointer transition-colors duration-200 ${
          dragOver ? "border-[#1D4D3B] bg-[#1D4D3B]/[0.04]" : "border-black/15 hover:border-black/30 bg-[#FAFAF9]"
        }`}
      >
        <UploadCloud className={`w-6 h-6 ${dragOver ? "text-[#1D4D3B]" : "text-black/30"}`} aria-hidden="true" />
        <p className="text-sm text-[#0A0A0A]">{t("vm.dropHint")}</p>
        <p className="text-xs text-[#737373]">{t("vm.dropSub", { max: 500 })}</p>
        <input ref={fileRef} type="file" multiple accept={ACCEPT_VIDEOS} className="hidden"
               onChange={onPick} data-testid="horse-video-input" />
        <input ref={replaceRef} type="file" accept={ACCEPT_VIDEOS} className="hidden"
               onChange={onReplacePick} data-testid="video-replace-input" />
      </div>

      {/* URL import */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30" aria-hidden="true" />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); importUrl(); } }}
            placeholder={t("vm.urlPlaceholder")}
            data-testid="video-url-input"
            className="rounded-xl pl-9 bg-white"
          />
        </div>
        <Button type="button" variant="outline" onClick={importUrl} disabled={importing || !url.trim()}
                data-testid="video-url-import-button" className="rounded-full h-10 px-5">
          {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
                     : <Youtube className="w-4 h-4 mr-1.5" aria-hidden="true" />}
          {t("vm.import")}
        </Button>
      </div>

      {/* in-flight / recoverable uploads */}
      {uploads.map((u) => (
        <div
          key={u.key}
          className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${
            u.status === "error" ? "border-red-200 bg-red-50/60" : "border-black/5 bg-white"
          }`}
          data-testid={u.status === "error" ? "video-upload-error" : "video-upload-progress"}
        >
          {u.status === "error" ? (
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" aria-hidden="true" />
          ) : (
            <Loader2 className="w-4 h-4 animate-spin text-[#1D4D3B] shrink-0" aria-hidden="true" />
          )}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-xs font-medium text-[#0A0A0A] truncate">{u.name}</span>
            {u.status === "error" && (
              <span className="text-[11px] text-red-700">{t("vm.uploadInterrupted")}</span>
            )}
          </div>
          {u.status === "error" ? (
            <span className="flex items-center gap-2 shrink-0">
              <Button type="button" size="sm" variant="outline" onClick={() => retryUpload(u)}
                      data-testid="video-upload-retry-button"
                      className="rounded-full h-8 px-3 text-xs border-black/10 hover:bg-black/5">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                {t("vm.retryUpload")}
              </Button>
              <button type="button" onClick={() => discardUpload(u)} aria-label={t("common.delete")}
                      data-testid="video-upload-discard-button"
                      className="w-7 h-7 rounded-full flex items-center justify-center text-black/30 hover:text-red-600 hover:bg-red-100 transition-colors duration-150">
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </span>
          ) : (
            <>
              <div className="w-36 h-1.5 rounded-full bg-black/8 overflow-hidden">
                <div className="h-full bg-[#1D4D3B] rounded-full transition-[width] duration-300" style={{ width: `${u.pct}%` }} />
              </div>
              <span className="text-[11px] tabular-nums text-[#737373] w-9 text-right">{u.pct}%</span>
            </>
          )}
        </div>
      ))}

      {/* ordered list — drag to reorder */}
      {value.length > 0 && (
        <Reorder.Group axis="y" values={value} onReorder={onChange} className="flex flex-col gap-2"
                       data-testid="video-list">
          {value.map((v) => {
            const badge = providerBadge(v);
            const poster = videoPosterSrc(v, BACKEND_URL);
            const isMain = mainId === v.id;
            return (
              <Reorder.Item key={v.id} value={v}
                            className="rounded-xl border border-black/5 bg-white px-3 py-2.5 flex items-center gap-3 cursor-grab active:cursor-grabbing"
                            data-testid={`video-row-${v.id}`}>
                <GripVertical className="w-4 h-4 text-black/20 shrink-0" aria-hidden="true" />
                <div className="w-20 h-12 rounded-lg overflow-hidden bg-[#0A0A0A] shrink-0 flex items-center justify-center">
                  {poster ? (
                    <img src={poster} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <Film className="w-4 h-4 text-white/40" aria-hidden="true" />
                  )}
                </div>
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-[#0A0A0A] truncate">
                      {v.title || v.original_filename}
                    </span>
                    <span className={`px-2 h-5 inline-flex items-center rounded-full text-[10px] font-semibold shrink-0 ${badge.cls}`}>
                      {badge.label}
                    </span>
                    {v.video_status === "processing" && (
                      <span className="px-2 h-5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold shrink-0"
                            data-testid={`video-processing-${v.id}`}>
                        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                        {t("vm.processing")}
                      </span>
                    )}
                    {v.video_status === "ready" && (
                      <span className="px-2 h-5 inline-flex items-center gap-1 rounded-full bg-[#1D4D3B]/10 text-[#1D4D3B] text-[10px] font-semibold shrink-0"
                            data-testid={`video-ready-${v.id}`}>
                        <Check className="w-3 h-3" aria-hidden="true" />
                        {t("vm.available")}
                      </span>
                    )}
                    {v.video_status === "error" && (
                      <span className="inline-flex items-center gap-1.5 shrink-0">
                        <span className="px-2 h-5 inline-flex items-center rounded-full bg-red-100 text-red-700 text-[10px] font-semibold"
                              data-testid={`video-error-${v.id}`}>
                          {t("vm.processingFailed")}
                        </span>
                        {v.source === "upload" && (
                          <button
                            type="button"
                            data-testid={`video-error-retry-${v.id}`}
                            onClick={() => beginReplace(v)}
                            className="text-[10px] font-semibold text-[#0A0A0A]/60 underline underline-offset-2 hover:text-[#0A0A0A] transition-colors duration-150"
                          >
                            {t("vm.retry")}
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                  <Select value={v.category || "none"} onValueChange={(c) => setCategory(v.id, c === "none" ? "" : c)}>
                    <SelectTrigger
                      data-testid={`horse-clip-category-${v.id}`}
                      aria-label={t("med.category")}
                      className="h-6 w-fit rounded-full bg-black/5 text-[#0A0A0A] text-[11px] px-2 border-0 shadow-none gap-1 focus:ring-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="none">{t("med.uncategorized")}</SelectItem>
                      {VIDEO_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(`vidcat.${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => onMainChange(isMain ? null : v.id)}
                        aria-label={t("vm.setMain")} aria-pressed={isMain}
                        data-testid={`video-main-toggle-${v.id}`}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${
                          isMain ? "bg-[#1D4D3B] text-white" : "text-black/25 hover:text-[#1D4D3B] hover:bg-[#1D4D3B]/10"
                        }`}>
                  <Star className={`w-4 h-4 ${isMain ? "fill-current" : ""}`} aria-hidden="true" />
                </button>
                {v.source === "upload" && (
                  <button type="button" onClick={() => beginReplace(v)} aria-label={t("vm.replace")}
                          data-testid={`video-replace-${v.id}`}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-black/25 hover:text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200">
                    <RefreshCw className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
                <button type="button" onClick={() => remove(v)} aria-label={t("common.delete")}
                        data-testid={`video-delete-${v.id}`}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-black/25 hover:text-red-600 hover:bg-red-50 transition-colors duration-200">
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                </button>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      )}
      {value.length > 1 && <p className="text-[11px] text-[#737373]">{t("vm.reorderHint")}</p>}
    </div>
  );
};
